import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { getProjects, getProjectFinancings, createProjectFinancing, updateProjectFinancing, deleteProjectFinancing } from '../lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Save, Trash2, Plus, X, TrendingUp, TrendingDown, DollarSign } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';

const ProjectFinancing = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const projectId = searchParams.get('project') || '';
  
  const [projects, setProjects] = useState([]);
  const [financings, setFinancings] = useState([]);
  const [selectedProject, setSelectedProject] = useState(projectId);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentFinancing, setCurrentFinancing] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    initial_investment: 0,
    project_duration_years: 10,
    discount_rate: 10,
    cash_flows: [],
    financing_amount: 0,
    interest_rate: 5,
    loan_duration_years: 5
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      fetchFinancings();
    }
  }, [selectedProject]);

  useEffect(() => {
    if (id && financings.length > 0) {
      const pf = financings.find(f => f.id === id);
      if (pf) {
        setCurrentFinancing(pf);
        setFormData({
          name: pf.name,
          description: pf.description || '',
          initial_investment: pf.initial_investment,
          project_duration_years: pf.project_duration_years,
          discount_rate: pf.discount_rate,
          cash_flows: pf.cash_flows || [],
          financing_amount: pf.financing_details?.amount || 0,
          interest_rate: pf.financing_details?.interest_rate || 5,
          loan_duration_years: pf.financing_details?.duration_years || 5
        });
      }
    }
  }, [id, financings]);

  const fetchProjects = async () => {
    try {
      const response = await getProjects();
      setProjects(response.data);
      if (!selectedProject && response.data.length > 0) {
        setSelectedProject(response.data[0].id);
      }
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const fetchFinancings = async () => {
    try {
      const response = await getProjectFinancings(selectedProject);
      setFinancings(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleSave = async () => {
    if (!selectedProject) {
      toast.error('Seleziona un progetto');
      return;
    }
    if (!formData.name) {
      toast.error('Inserisci un nome');
      return;
    }

    setSaving(true);
    try {
      const data = {
        project_id: selectedProject,
        name: formData.name,
        description: formData.description,
        initial_investment: parseFloat(formData.initial_investment),
        project_duration_years: parseInt(formData.project_duration_years),
        discount_rate: parseFloat(formData.discount_rate),
        cash_flows: formData.cash_flows.map(cf => parseFloat(cf) || 0),
        financing_amount: parseFloat(formData.financing_amount) || 0,
        interest_rate: parseFloat(formData.interest_rate),
        loan_duration_years: parseInt(formData.loan_duration_years)
      };

      let response;
      if (currentFinancing) {
        response = await updateProjectFinancing(currentFinancing.id, data);
      } else {
        response = await createProjectFinancing(data);
      }
      
      setCurrentFinancing(response.data);
      fetchFinancings();
      toast.success(t('common.success'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!currentFinancing || !window.confirm('Sei sicuro di voler eliminare questa analisi?')) return;
    
    try {
      await deleteProjectFinancing(currentFinancing.id);
      setCurrentFinancing(null);
      resetForm();
      fetchFinancings();
      toast.success(t('common.success'));
      navigate(`/project-financing?project=${selectedProject}`);
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      initial_investment: 0,
      project_duration_years: 10,
      discount_rate: 10,
      cash_flows: [],
      financing_amount: 0,
      interest_rate: 5,
      loan_duration_years: 5
    });
  };

  const handleNewFinancing = () => {
    setCurrentFinancing(null);
    resetForm();
    navigate(`/project-financing?project=${selectedProject}`);
  };

  const updateField = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const updateCashFlow = (index, value) => {
    const newCashFlows = [...formData.cash_flows];
    newCashFlows[index] = value;
    setFormData({ ...formData, cash_flows: newCashFlows });
  };

  const addCashFlow = () => {
    setFormData({ ...formData, cash_flows: [...formData.cash_flows, 0] });
  };

  const removeCashFlow = (index) => {
    const newCashFlows = formData.cash_flows.filter((_, i) => i !== index);
    setFormData({ ...formData, cash_flows: newCashFlows });
  };

  const getChartData = () => {
    if (!currentFinancing) return [];
    const data = [{ year: 0, cashFlow: -currentFinancing.initial_investment, cumulative: -currentFinancing.initial_investment }];
    let cumulative = -currentFinancing.initial_investment;
    
    currentFinancing.cash_flows.forEach((cf, index) => {
      cumulative += cf;
      data.push({
        year: index + 1,
        cashFlow: cf,
        cumulative: cumulative
      });
    });
    
    return data;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="project-financing-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
        <div className="flex items-start gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">{t('pf.title')}</h1>
            <p className="text-muted-foreground mt-1">
              {currentFinancing ? currentFinancing.name : 'Nuova analisi'}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleNewFinancing} data-testid="new-financing-btn">
            <Plus size={16} className="mr-2" />
            Nuovo
          </Button>
          {currentFinancing && (
            <Button variant="outline" onClick={handleDelete} className="text-destructive" data-testid="delete-financing-btn">
              <Trash2 size={16} className="mr-2" />
              {t('common.delete')}
            </Button>
          )}
          <Button onClick={handleSave} disabled={saving} className="bg-gradient-to-r from-violet-600 to-indigo-600" data-testid="save-btn">
            <Save size={16} className="mr-2" />
            {saving ? t('common.loading') : t('common.save')}
          </Button>
        </div>
      </div>

      {/* Project Selection */}
      <Card className="border-0 shadow-lg">
        <CardContent className="p-6">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Progetto</Label>
              <Select value={selectedProject} onValueChange={setSelectedProject}>
                <SelectTrigger data-testid="project-select">
                  <SelectValue placeholder="Seleziona progetto" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name} - {p.company_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Analisi Salvate</Label>
              <Select 
                value={currentFinancing?.id || ''} 
                onValueChange={(value) => {
                  navigate(`/project-financing/${value}?project=${selectedProject}`);
                }}
              >
                <SelectTrigger data-testid="saved-financings-select">
                  <SelectValue placeholder="Seleziona analisi" />
                </SelectTrigger>
                <SelectContent>
                  {financings.map(f => (
                    <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="investment" className="space-y-6">
        <TabsList className="bg-muted/50 p-1 rounded-xl w-full sm:w-auto">
          <TabsTrigger value="investment" className="rounded-lg" data-testid="tab-investment">Investimento</TabsTrigger>
          <TabsTrigger value="cashflows" className="rounded-lg" data-testid="tab-cashflows">{t('pf.cashFlows')}</TabsTrigger>
          <TabsTrigger value="results" className="rounded-lg" data-testid="tab-results">Risultati</TabsTrigger>
          <TabsTrigger value="financing" className="rounded-lg" data-testid="tab-financing">{t('pf.financing')}</TabsTrigger>
        </TabsList>

        {/* Investment Tab */}
        <TabsContent value="investment">
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle>Dati Investimento</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('pf.name')}</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => updateField('name', e.target.value)}
                    placeholder="Nuovo impianto produttivo"
                    data-testid="input-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Descrizione</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => updateField('description', e.target.value)}
                    placeholder="Descrizione opzionale"
                    data-testid="input-description"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>{t('pf.initialInvestment')}</Label>
                  <Input
                    type="number"
                    value={formData.initial_investment}
                    onChange={(e) => updateField('initial_investment', e.target.value)}
                    data-testid="input-investment"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('pf.duration')}</Label>
                  <Input
                    type="number"
                    value={formData.project_duration_years}
                    onChange={(e) => updateField('project_duration_years', e.target.value)}
                    min={1}
                    max={30}
                    data-testid="input-duration"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('pf.discountRate')}</Label>
                  <Input
                    type="number"
                    value={formData.discount_rate}
                    onChange={(e) => updateField('discount_rate', e.target.value)}
                    step="0.1"
                    data-testid="input-discount"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Cash Flows Tab */}
        <TabsContent value="cashflows">
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>{t('pf.cashFlows')}</CardTitle>
                <Button variant="outline" size="sm" onClick={addCashFlow} data-testid="add-cashflow-btn">
                  <Plus size={16} className="mr-2" />
                  Aggiungi Anno
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {formData.cash_flows.length > 0 ? (
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {formData.cash_flows.map((cf, index) => (
                    <div key={index} className="space-y-2">
                      <Label className="flex items-center justify-between">
                        Anno {index + 1}
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-6 w-6" 
                          onClick={() => removeCashFlow(index)}
                        >
                          <X size={14} />
                        </Button>
                      </Label>
                      <Input
                        type="number"
                        value={cf}
                        onChange={(e) => updateCashFlow(index, e.target.value)}
                        data-testid={`cashflow-${index}`}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <p>Nessun flusso di cassa inserito</p>
                  <Button variant="outline" className="mt-4" onClick={addCashFlow}>
                    <Plus size={16} className="mr-2" />
                    Aggiungi il primo flusso
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Results Tab */}
        <TabsContent value="results">
          {currentFinancing ? (
            <div className="space-y-6">
              {/* KPI Cards */}
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                <Card className="border-0 shadow-lg">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-muted-foreground">{t('pf.npv')}</span>
                      {currentFinancing.npv >= 0 ? (
                        <TrendingUp className="text-green-500" size={20} />
                      ) : (
                        <TrendingDown className="text-red-500" size={20} />
                      )}
                    </div>
                    <p className={`text-2xl font-bold ${currentFinancing.npv >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      €{currentFinancing.npv?.toLocaleString()}
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-lg">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-muted-foreground">{t('pf.irr')}</span>
                      {currentFinancing.irr && currentFinancing.irr > currentFinancing.discount_rate ? (
                        <TrendingUp className="text-green-500" size={20} />
                      ) : (
                        <TrendingDown className="text-red-500" size={20} />
                      )}
                    </div>
                    <p className="text-2xl font-bold">
                      {currentFinancing.irr ? `${currentFinancing.irr}%` : 'N/A'}
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-lg">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-muted-foreground">{t('pf.payback')}</span>
                      <DollarSign className="text-blue-500" size={20} />
                    </div>
                    <p className="text-2xl font-bold">
                      {currentFinancing.payback_period ? `${currentFinancing.payback_period} anni` : 'N/A'}
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-lg">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-muted-foreground">{t('pf.initialInvestment')}</span>
                      <DollarSign className="text-violet-500" size={20} />
                    </div>
                    <p className="text-2xl font-bold">
                      €{currentFinancing.initial_investment?.toLocaleString()}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Chart */}
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle>Flussi di Cassa Cumulativi</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={getChartData()}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="year" stroke="#64748b" label={{ value: 'Anno', position: 'bottom' }} />
                        <YAxis stroke="#64748b" tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                        <Tooltip 
                          formatter={(value) => `€${value.toLocaleString()}`}
                          contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                        />
                        <ReferenceLine y={0} stroke="#94a3b8" />
                        <Bar 
                          dataKey="cashFlow" 
                          name="Flusso di Cassa"
                          fill={(entry) => entry.cashFlow >= 0 ? '#22c55e' : '#ef4444'}
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card className="border-0 shadow-lg">
              <CardContent className="py-12 text-center text-muted-foreground">
                <p>Salva l'analisi per visualizzare i risultati</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Financing Tab */}
        <TabsContent value="financing">
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle>Dettagli Finanziamento</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>{t('pf.amount')}</Label>
                  <Input
                    type="number"
                    value={formData.financing_amount}
                    onChange={(e) => updateField('financing_amount', e.target.value)}
                    data-testid="input-financing-amount"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('pf.interestRate')}</Label>
                  <Input
                    type="number"
                    value={formData.interest_rate}
                    onChange={(e) => updateField('interest_rate', e.target.value)}
                    step="0.1"
                    data-testid="input-interest-rate"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('pf.loanDuration')}</Label>
                  <Input
                    type="number"
                    value={formData.loan_duration_years}
                    onChange={(e) => updateField('loan_duration_years', e.target.value)}
                    min={1}
                    max={30}
                    data-testid="input-loan-duration"
                  />
                </div>
              </div>

              {currentFinancing?.financing_details?.amount > 0 && (
                <div className="mt-6 p-6 bg-muted/30 rounded-xl">
                  <h4 className="font-semibold mb-4">Riepilogo Finanziamento</h4>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">{t('pf.annualPayment')}</p>
                      <p className="text-xl font-bold">€{currentFinancing.financing_details.annual_payment?.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">{t('pf.totalInterest')}</p>
                      <p className="text-xl font-bold">€{currentFinancing.financing_details.total_interest?.toLocaleString()}</p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ProjectFinancing;
